// eslint-disable-next-line no-use-before-define
import React, { Fragment, useState, useEffect, useCallback } from 'react';
import { useRouteMatch, useHistory } from 'react-router-dom';
import { ROPatientCarePlanRoute } from '../Interface';
import { BaseAutocomplete, BaseTextField } from 'shared-components/components/FormFields';
import ROPatientCarePlanPageFooter from '../Footer';
import { HelpOutline, AddCircle } from '@mui/icons-material';
import './SiteSetupPage.scss';
import { CAREPLAN_PAGES } from '../Constants';
import { useMutation, useQuery } from '@apollo/client';
import { getOptionByValue } from '../DiagnosisPages/Utils';
import {
  DELETE_SITE,
  DELETE_SITE_GROUP,
  UPDATE_SITE,
  GET_CAREPLAN,
  UPDATE_TREATMENT_SITE,
  SITE_NAME_VARIATION,
  GET_CAREPLAN_PRESCRIPTIONS,
  MOVE_SITE_TO_SIM_GROUP,
  CLEAN_CAREPLAN,
  UPDATE_PRESCRIPTION_LATERALITY,
} from '../Queries';
import { LIST_TREATMENT_SITES_ORDERBY_USAGE } from '../TemplatePage/TemplatePageQueries';
import { LOAD_CAREPLAN_LIST_DATA } from '../ListData/ListQueries';
import { filterListdata, DEFAULT_OPTION } from '../ListData/ListData';
import { validateField } from '../ValidationEngine';
import {
  getSiteRepresentation,
  isSpecifySite,
  getPrescriptionsWithSiteGroups,
  templatesWithValues,
  getPrescriptionsWithSites,
  parseLateralityValue,
} from '../Common';
import { CareplanBanners } from '../Banner/Banner';
import ROPatientCarePathSidePanel from 'op-pages/RO/Careplan/SidePanel/SidePanel';
import { LoadingSpinner } from 'shared-components/components';
import CareplanContainer from '../Container';
import { GET_PEER_REVIEW, GET_DIAGNOSIS_FROM_CAREPLAN } from '../SidePanel/SidePanelQueries';
import { logMissingDiagnosisCode } from '../Common';
import { Logger } from 'shared-components/utils';
import Container from '@mui/material/Container';
import {
  useTheme,
  Stack,
  Grid,
  Typography,
  Tooltip,
  MenuItem,
  styled,
  TooltipProps,
  tooltipClasses,
} from '@mui/material';
import BaseSelect, { SelectOptionType } from 'shared-components/components/FormFields/ROSelect/BaseSelect';
import NotificationSnackbar from 'shared-components/components/NotificationSnackbar';
import MoveButton from './MoveButton';
import DeleteSiteButton from './DeleteSiteButton';

const LIST_OPTIONS = {
  LATERALITY: 'lateralitySite',
};
let pressedContinue = false;
const isFieldValid = (field: any) => {
  return pressedContinue ? validateField(field) : true;
};

const sortOptionType = (a: SelectOptionType, b: SelectOptionType) => {
  if (a.value < b.value) return -1;
  if (a.value > b.value) return 1;
  return 0;
};

const logger = new Logger('SiteSetupPage.tsx');

const LightTooltip = styled(({ className, ...props }: TooltipProps) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    color: 'white',
    boxShadow: theme.shadows[1],
    fontSize: 13,
    lineHeight: '14px',
  },
}));

const ROPatientCarePathSiteSetupPage = (): JSX.Element => {
  const match = useRouteMatch<ROPatientCarePlanRoute>();
  const theme = useTheme();
  const { careplanId, id: patientId } = match.params;
  const [lateralitySiteOptions, setLateralitySiteOptions] = useState<SelectOptionType[]>(DEFAULT_OPTION);
  const [locationList, setLocationList] = useState<any[]>([]);
  const [lateralityState, setLateralityState] = useState<any>([]);
  const [activeLateralitySite, setActiveLateralitySite] = useState({});
  const [, updateState] = useState();
  // @ts-ignore allow empty object
  const forceUpdate = useCallback(() => updateState({}), []);
  const history = useHistory();
  // Surfaces a snackbar when a site delete fails. In-flight state is owned by
  // each DeleteSiteButton so a second click can't re-fire a delete mid-request
  // (which previously triggered Site.DoesNotExist - Sentry UK-HORIZON-PROD-31A).
  const [deleteError, setDeleteError] = useState<boolean>(false);

  const { loading, data } = useQuery(GET_CAREPLAN_PRESCRIPTIONS, {
    fetchPolicy: 'network-only',
    variables: { careplanId },
  });

  const [moveSiteToSimGroup] = useMutation(MOVE_SITE_TO_SIM_GROUP, {
    refetchQueries: [
      { query: GET_CAREPLAN_PRESCRIPTIONS, variables: { careplanId } },
      { query: GET_CAREPLAN, variables: { id: careplanId } },
      { query: GET_PEER_REVIEW, variables: { careplanId } },
    ],
  });
  useQuery(LOAD_CAREPLAN_LIST_DATA, {
    fetchPolicy: 'cache-first',
    variables: {
      listCategory: [LIST_OPTIONS.LATERALITY],
      patientId,
    },
    onCompleted: (data: any): void => {
      setLateralitySiteOptions([...filterListdata(data, LIST_OPTIONS.LATERALITY)]);
    },
  });

  const { data: careplan } = useQuery(GET_CAREPLAN, {
    fetchPolicy: 'network-only',
    variables: { id: careplanId },
  });

  useQuery(GET_DIAGNOSIS_FROM_CAREPLAN, {
    fetchPolicy: 'network-only',
    variables: { careplanId: careplanId },
    onCompleted: (data: any): void => {
      logMissingDiagnosisCode(logger, data.careplan, 'Site Setup');
    },
  });

  const { data: siteNameVariation } = useQuery(SITE_NAME_VARIATION, {
    fetchPolicy: 'network-only',
    variables: { careplanId },
    skip: loading,
  });

  const hasVariation = siteNameVariation?.siteNameVariation?.variation;

  const [deleteSite] = useMutation(DELETE_SITE, {
    awaitRefetchQueries: true,
    refetchQueries: [
      { query: GET_CAREPLAN_PRESCRIPTIONS, variables: { careplanId } },
      { query: GET_CAREPLAN, variables: { id: careplanId } },
      { query: GET_PEER_REVIEW, variables: { careplanId } },
      { query: SITE_NAME_VARIATION, variables: { careplanId } },
    ],
    onError: (error): void => {
      logger.error('handleDeleteSite', error);
      setDeleteError(true);
    },
  });
  const [deleteGroup] = useMutation(DELETE_SITE_GROUP, {
    refetchQueries: [
      { query: GET_CAREPLAN_PRESCRIPTIONS, variables: { careplanId } },
      { query: GET_CAREPLAN, variables: { id: careplanId } },
      { query: GET_PEER_REVIEW, variables: { careplanId } },
      { query: SITE_NAME_VARIATION, variables: { careplanId } },
    ],
  });

  const [cleanCareplan] = useMutation(CLEAN_CAREPLAN, {
    refetchQueries: [
      { query: GET_CAREPLAN_PRESCRIPTIONS, variables: { careplanId } },
      { query: GET_CAREPLAN, variables: { id: careplanId } },
      { query: GET_PEER_REVIEW, variables: { careplanId } },
    ],
  });
  const [updateSite] = useMutation(UPDATE_SITE, {
    refetchQueries: [
      { query: GET_CAREPLAN_PRESCRIPTIONS, variables: { careplanId } },
      { query: GET_CAREPLAN, variables: { id: careplanId } },
      { query: GET_PEER_REVIEW, variables: { careplanId } },
    ],
  });

  const [updatePrescriptionLaterality] = useMutation(UPDATE_PRESCRIPTION_LATERALITY, {
    refetchQueries: [
      { query: GET_CAREPLAN_PRESCRIPTIONS, variables: { careplanId } },
      { query: GET_CAREPLAN, variables: { id: careplanId } },
    ],
  });

  const [updateTreatmentSite] = useMutation(UPDATE_TREATMENT_SITE, {
    refetchQueries: [
      { query: GET_CAREPLAN_PRESCRIPTIONS, variables: { careplanId } },
      { query: GET_CAREPLAN, variables: { id: careplanId } },
      { query: SITE_NAME_VARIATION, variables: { careplanId } },
      { query: GET_PEER_REVIEW, variables: { careplanId } },
    ],
  });

  const { data: treatmentSitesData } = useQuery(LIST_TREATMENT_SITES_ORDERBY_USAGE);

  useEffect(() => {
    pressedContinue = false;
  }, []);

  useEffect(() => {
    const errorElems = document.getElementsByClassName('icon-form-error');
    if (errorElems && errorElems.length > 0) {
      errorElems[0].scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
        inline: 'start',
      });
      (errorElems[0] as HTMLElement).focus({ preventScroll: true });
    } else {
      pressedContinue = false;
    }
  });

  if (loading)
    return (
      <LoadingSpinner loadingText={'Loading Site Setup'} subtitle={'Please wait while we set things up for you'} />
    );

  const prescriptionsByCareplan: any[] = data?.prescriptionsByCareplan ?? [];
  if (!prescriptionsByCareplan.length) {
    return (
      <CareplanContainer>
        <Typography variant="h6" color="textSecondary" align="center">
          No prescriptions found. Please add prescriptions from the Template Selection page.
        </Typography>
      </CareplanContainer>
    );
  }

  // group the site groups by the careplan template id
  const templates = getPrescriptionsWithSites(prescriptionsByCareplan);

  const lateralityIndex = (siteId: string) => {
    return lateralityState.findIndex((item: any) => item.id === siteId);
  };

  const treatmentSiteList =
    treatmentSitesData?.mostUsedSiteReferenceList?.map((site: any): SelectOptionType => {
      return { label: site.treatmentSite, value: site.treatmentSite };
    }) || [];

  const setLaterality = (siteId: string, option: string, siteUpdate = true): void => {
    const newLateralityState = lateralityState;
    const removeValueIndex = lateralityIndex(siteId);
    if (removeValueIndex !== -1) {
      newLateralityState.splice(removeValueIndex, 1);
    }
    newLateralityState.push({ id: siteId, value: option });
    setLateralityState(newLateralityState);
    if (siteUpdate) {
      updateSite({ variables: { siteId, laterality: option } });
    }
  };

  const lateralityValue = (siteId: string, laterality: any): string => {
    if (lateralityIndex(siteId) !== -1) {
      return lateralityState[lateralityIndex(siteId)].value;
    }
    return parseLateralityValue(laterality);
  };

  const getLocationOption = (site: any): string => {
    const location = site.sitevaluesSet.filter((values: any): any => values.field.name === 'Location');
    return location[0] ? JSON.parse(location[0].value) : '';
  };

  const setlocationFields = (id: string, newValue: string): void => {
    const Idx = locationList.findIndex((obj: any) => obj.id === id);
    const newValues = [...locationList];
    Idx >= 0 ? (newValues[Idx].value = newValue) : newValues.push({ id, value: newValue });
    setLocationList(newValues);
  };

  const siteLocationLookup = (idToMatch: string, defaultValue: any) => {
    const matchedElement = locationList.find((x: any) => x.id === idToMatch);
    return matchedElement ? matchedElement.value : defaultValue;
  };
  const checkLocationOption = (site: any): boolean => {
    const db_location = getLocationOption(site);
    const state_location = siteLocationLookup(site.id, getLocationOption(site));
    if (db_location !== state_location) {
      return false;
    }
    return Boolean(db_location);
  };
  // Returns the mutation promise so DeleteSiteButton can track its own in-flight
  // state; awaitRefetchQueries keeps that promise pending until the refetch
  // completes, so the button stays busy until the site actually disappears.
  const handleDeleteSite = (siteId: number): Promise<unknown> => deleteSite({ variables: { id: siteId } });

  const isSpecifyComplete = !templates
    .map((template: any) =>
      template.sitegroupSet.map((sitegroupSet: any) =>
        sitegroupSet.siteSet.map((siteSet: any) =>
          isSpecifySite(siteSet.treatmentSite.treatmentSite) ? checkLocationOption(siteSet) : true,
        ),
      ),
    )
    .flat(2)
    .includes(false);

  const isLateralityComplete = templates.every((template: any) =>
    template.sitegroupSet.every((sitegroupSet: any) =>
      sitegroupSet.siteSet.every((siteSet: any) => {
        const laterality = siteSet.sitevaluesSet.filter((values: any) => values.field.name === 'Laterality');
        return lateralityValue(siteSet.id, laterality) !== '';
      }),
    ),
  );

  const isSetupValid = () => {
    return pressedContinue && isSpecifyComplete && isLateralityComplete;
  };

  const getTemplateGroupRepresentation = (template: any) => {
    const sites = template.sitegroupSet
      .map((sitegroup: any) => sitegroup.siteSet)
      .flat()
      .filter(Boolean);
    const siteNames = sites
      .map((site: any) => getSiteRepresentation(site))
      .slice(0, 3)
      .join(', ');
    return sites.length > 3 ? siteNames + ` + ${sites.length - 3}` : siteNames;
  };
  // filter out prescriptions that have no site groups with aleast one site in them.
  const prescriptionIds = templates.map((template: any) => template.id);

  return (
    <Fragment>
      <div className="main-container-parent-wrapper">
        <div className="main-container-wrapper">
          {loading && <div className="main-container">Loading </div>}
          <CareplanBanners
            data={careplan}
            variation={hasVariation}
            refetchQueriesList={[
              { query: GET_CAREPLAN_PRESCRIPTIONS, variables: { careplanId } },
              { query: GET_CAREPLAN, variables: { id: careplanId } },
              { query: SITE_NAME_VARIATION, variables: { careplanId } },
            ]}
          />
          <CareplanContainer className={'carepath-site-setup-page'}>
            <Typography variant="h5" data-testid="site-setup-header">
              Site Setup
            </Typography>
            <Container className="site-setup-page-sites-container">
              <Typography paddingTop={1} sx={{ paddingBottom: '8px' }} variant="body2">
                By default, all sites in a prescription share the same simulation and voluming details. Simulations can
                be customised with Sim Groups. Treatment details such as technique, dose, and fractions are per site.
              </Typography>
              <Grid container sx={{ background: theme.palette.grey[100] }}>
                <Grid sx={{ padding: '16px' }} xs={3} item key="treatmentSite">
                  <Typography sx={{ fontSize: '16px' }} variant="subtitle1">
                    Treatment Site*
                  </Typography>
                </Grid>
                <Grid sx={{ padding: '16px' }} xs={3} item key="laterality">
                  <Typography sx={{ fontSize: '16px' }} variant="subtitle1">
                    Laterality*
                  </Typography>
                </Grid>
                <Grid sx={{ padding: '16px' }} xs={2} item key="simGroup">
                  <Stack direction="row" alignItems="center" gap={1}>
                    <Typography sx={{ fontSize: '16px' }} variant="subtitle1">
                      Sim Group
                    </Typography>
                    <LightTooltip
                      arrow
                      title="Sim groups outline whether sites within the same prescription should be simulated together or separately. Instructions regarding the chosen configuration will be shown on the Simulation PDF. Sim group numbering resets in subsequent prescriptions.">
                      <HelpOutline
                        sx={{
                          cursor: 'pointer',
                          fill: theme.palette.info.main,
                        }}
                      />
                    </LightTooltip>
                  </Stack>
                </Grid>
                <Grid sx={{ padding: '16px', textAlign: 'center' }} xs={4} item key="action">
                  <Typography sx={{ fontSize: '16px' }} variant="subtitle1">
                    Action
                  </Typography>
                </Grid>
              </Grid>
              {getPrescriptionsWithSiteGroups(templates).map((template: any, tIndex: number): JSX.Element => {
                // Flatten all sites from site groups and sort by their order ranking
                const validSiteGroups = templatesWithValues(template.sitegroupSet);
                const simGroupsLength = validSiteGroups.length;
                // Flatten all sites from site groups and sort by their order ranking
                const sites = validSiteGroups
                  .map((sitegroup: any) => sitegroup.siteSet)
                  .flat()
                  .filter(Boolean)
                  .sort((a: any, b: any) => a.orderRanking - b.orderRanking);
                // Map each site to its parent sitegroup index for sim group display
                const sitegroupIndices = validSiteGroups
                  .flatMap((sitegroup: any, sitegroupIndex: number) =>
                    sitegroup.siteSet.map((site: any) => ({ sitegroupIndex, siteIndex: site.orderRanking })),
                  )
                  .sort((a: any, b: any) => a.siteIndex - b.siteIndex)
                  .map((item: any) => item.sitegroupIndex);
                const siteGroupIds = validSiteGroups.map((sitegroup: any) => sitegroup.id);
                const simGroupNames =
                  sites.length > 1
                    ? Array.from({ length: simGroupsLength }, (_, index) => `Sim ${index + 1}`).concat('New Sim')
                    : ['Sim 1'];
                if (sites.length === 0) return <Fragment key={`${tIndex} `} />;
                return (
                  <Fragment key={`${tIndex} `}>
                    {template && (
                      <Grid container className="site-setup-page-template-header" key={`row - ${tIndex} `}>
                        <Grid item md={12} key={`col - ${tIndex} `}>
                          <Typography
                            variant="body1"
                            paddingLeft={2}
                            paddingTop={1}
                            paddingBottom={1}
                            className={'site-setup-template-group-header'}>
                            {`Prescription ${tIndex + 1} `}: {getTemplateGroupRepresentation(template)}
                          </Typography>
                        </Grid>
                      </Grid>
                    )}
                    {sites.map((site: any, sIndex: number): JSX.Element => {
                      const laterality = site.sitevaluesSet.filter(
                        (sitevalue: any): any => sitevalue.field.name === 'Laterality',
                      );
                      const isLateralitySet = lateralityValue(site.id, laterality) !== '';
                      const validateLaterality = !isFieldValid(isLateralitySet);
                      const locationInvalid = !isFieldValid(siteLocationLookup(site.id, getLocationOption(site)));
                      return (
                        <Grid
                          container
                          key={`site - ${sIndex} `}
                          alignItems="flex-start"
                          sx={{ border: `1px solid ${theme.palette.grey[300]} `, borderTop: 'none' }}>
                          <Grid sx={{ padding: '16px' }} item xs={3} key={`site - col - loca - ${sIndex} `}>
                            <BaseAutocomplete
                              id="treatmentSites"
                              options={[...treatmentSiteList].sort(sortOptionType)}
                              value={getOptionByValue(treatmentSiteList, site.treatmentSite.treatmentSite)}
                              onChange={(option: SelectOptionType | string): void => {
                                const value = typeof option === 'string' ? option : option?.value;
                                updateTreatmentSite({
                                  variables: {
                                    siteId: site.id,
                                    treatmentSite: value || '',
                                  },
                                });
                              }}
                              disableClearable
                              inputProps={{
                                error: !isFieldValid(site.treatmentSite.treatmentSite),
                                helperText: !isFieldValid(site.treatmentSite.treatmentSite)
                                  ? 'Please select a treatment site'
                                  : '',
                              }}
                            />
                            {isSpecifySite(site.treatmentSite.treatmentSite) && (
                              <div className={`ro-site-setup-location ${locationInvalid ? 'invalid' : 'valid'} `}>
                                <BaseTextField
                                  id="location"
                                  fullWidth
                                  placeholder={'Please specify location'}
                                  value={siteLocationLookup(site.id, getLocationOption(site))}
                                  onChange={(event: React.ChangeEvent<HTMLInputElement>): void => {
                                    setlocationFields(site.id, event.target.value);
                                  }}
                                  onBlur={() => {
                                    updateSite({
                                      variables: {
                                        siteId: site.id,
                                        location: siteLocationLookup(site.id, site.location),
                                      },
                                    });
                                  }}
                                  error={locationInvalid}
                                  required
                                  sx={{
                                    paddingBottom: '20px', // Jank to make row wide enough
                                  }}
                                />
                              </div>
                            )}
                          </Grid>
                          <Grid sx={{ padding: '16px' }} item xs={3}>
                            <div
                              key={`site - col - lat - ${sIndex} `}
                              style={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
                              <BaseSelect
                                data-testid={`laterality-${tIndex}-${sIndex}`}
                                error={validateLaterality}
                                helperText={'Please choose a laterality'}
                                id={`laterality-${tIndex}-${sIndex}`}
                                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                                  if (lateralityValue(site.id, laterality) !== event.target.value) {
                                    setActiveLateralitySite((lateralitySite) => {
                                      return { ...lateralitySite, [template.id]: site.id };
                                    });
                                  }
                                  setLaterality(site.id, event.target.value, true);
                                }}
                                value={lateralityValue(site.id, laterality)}
                                placeholder="Please Choose"
                                // @ts-expect-error standard props don't like outlined
                                variant="outlined"
                                sx={{
                                  fontSize: '14px',
                                  color: validateLaterality ? theme.palette.error.main : 'initial',
                                  borderColor: theme.palette.primary.main,
                                  fontWeight: validateLaterality ? 'bold' : 'initial',
                                  width: '100%',
                                  paddingLeft: '0',
                                }}
                                options={lateralitySiteOptions}
                                inputProps={{ fontSize: '14px' }}></BaseSelect>
                            </div>
                            {sites.length > 1 &&
                              activeLateralitySite.hasOwnProperty(template.id) &&
                              // @ts-ignore
                              activeLateralitySite[template.id] === site.id && (
                                <Typography
                                  variant="body1"
                                  sx={{
                                    cursor: 'pointer',
                                    marginTop: '4px',
                                    ':hover': {
                                      textDecoration: 'underline',
                                    },
                                  }}
                                  onClick={() => {
                                    const latValue = lateralityValue(site.id, laterality);
                                    updatePrescriptionLaterality({
                                      variables: {
                                        prescriptionId: template.id,
                                        siteId: site.id,
                                        laterality: latValue,
                                      },
                                    });
                                    sites.forEach((site: any) => {
                                      setLaterality(site.id, latValue, false);
                                    });
                                    setActiveLateralitySite({});
                                  }}>
                                  Apply to all
                                </Typography>
                              )}
                          </Grid>
                          <Grid sx={{ padding: '16px' }} item xs={2} key={`site - col - sim - ${sIndex} `}>
                            <BaseTextField
                              select
                              value={
                                sitegroupIndices[sIndex] !== undefined ? siteGroupIds[sitegroupIndices[sIndex]] : ''
                              }
                              onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                                const simGroupId = event.target.value;
                                moveSiteToSimGroup({ variables: { siteId: site.id, simGroupId: simGroupId || null } });
                              }}
                              variant="standard"
                              disabled={sites.length === 1}
                              sx={{
                                width: '100%',
                                '& .MuiInputBase-root': { minHeight: '40px' },
                              }}>
                              {simGroupNames.map((name: string, index: number) => (
                                <MenuItem key={name} value={name === 'New Sim' ? null : siteGroupIds[index]}>
                                  <Stack direction="row" alignItems="center" gap={1}>
                                    {name === 'New Sim' && <AddCircle color="primary" />}
                                    {name}
                                  </Stack>
                                </MenuItem>
                              ))}
                            </BaseTextField>
                          </Grid>
                          <Grid sx={{ padding: '16px' }} item xs={4} key={`site - col - setup - ${sIndex} `}>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '20px', justifyContent: 'flex-end' }}>
                              <MoveButton
                                careplanId={careplanId}
                                siteId={site.id}
                                prescriptionId={template.id}
                                prescriptionIds={prescriptionIds}
                                disabled={sites.length === 1 && prescriptionIds.length === 1}
                              />
                              <DeleteSiteButton siteId={site.id} onDelete={handleDeleteSite} />
                            </div>
                          </Grid>
                        </Grid>
                      );
                    })}
                  </Fragment>
                );
              })}
            </Container>
          </CareplanContainer>
          <ROPatientCarePlanPageFooter
            onBack={(): void => {
              cleanCareplan({ variables: { careplanId } });
              history.push(
                `/radiation/patient/${patientId}/careplan/${careplanId}/${CAREPLAN_PAGES.TEMPLATE_SELECTION}`,
              );
            }}
            onNext={(): void => {
              pressedContinue = true;
              forceUpdate();
              if (!isSetupValid()) return;
              cleanCareplan({ variables: { careplanId } });
              history.push(`/radiation/patient/${patientId}/careplan/${careplanId}/${CAREPLAN_PAGES.SIMULATION}`);
            }}
            nextDisabled={prescriptionIds && prescriptionIds.length === 0}
          />
        </div>
        <ROPatientCarePathSidePanel />
      </div>
      <NotificationSnackbar
        open={deleteError}
        onClose={(): void => setDeleteError(false)}
        message="Couldn't delete the site. Please try again."
        type="error"
      />
    </Fragment>
  );
};

export default ROPatientCarePathSiteSetupPage;
